import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Alert } from "react-bootstrap";
import type {
  ModelPerformance as ModelPerformanceData,
  ModelRequestSecurities,
  ReturnPeriod,
  SecurityWithShortHistory,
  SecurityWithoutExpenseRatio,
} from "../../../api/src/models/models.service";
import Loading from "../Loading";
import FastTrackAttribution from "../components/FastTrackAttribution";
import { formatMetricPercent } from "../lib/numbers";
import { MODEL_PERFORMANCE_KEY, useQueryModelPerformance } from "./lib";

/**
 * The footnote of the "Expense Ratio" asterisk: the funds FastTrack sends no
 * expense ratio (or 0) for, with the name FastTrack sends when it sends one.
 * The API leaves stocks out: they have no fund expenses, and count as 0%
 * without a note. `countedAsZero` is false when the expense ratio is "N/A"
 * (no security has one), so the note does not claim a 0% was averaged in.
 */
export function missingExpenseRatioNote(
  securities: SecurityWithoutExpenseRatio[],
  countedAsZero = true,
) {
  const listed = securities
    .map(({ symbol, name }) => (name ? `${symbol} (${name})` : symbol))
    .join(", ");
  return countedAsZero
    ? `* No expense ratio or 0% for: ${listed} — counted as 0% in the weighted average.`
    : `* No expense ratio or 0% for: ${listed}.`;
}

/** Each return of the panel, in its order, with the label of its footnote. */
const RETURN_PERIODS: { period: ReturnPeriod; label: string }[] = [
  { period: "oneYear", label: "1-year" },
  { period: "threeYear", label: "3-year" },
  { period: "fiveYear", label: "5-year" },
];

/**
 * The securities that make `period`'s return "N/A" for lack of history: none
 * when the return has a value (it is not marked), or when it is "N/A" for
 * another reason (FastTrack sent no number).
 */
function shortHistoryFor(
  performance: Pick<
    ModelPerformanceData,
    "returns" | "securitiesWithShortHistory"
  >,
  period: ReturnPeriod,
) {
  return performance.returns[period] === null
    ? (performance.securitiesWithShortHistory ?? []).filter(
        ({ missingPeriods }) => missingPeriods.includes(period),
      )
    : [];
}

/**
 * The footnote of a "N/A†" return: the securities whose FastTrack history is
 * shorter than the period, each with the first day of its history, e.g.
 * "† 5-year return not available: DFAR (history since 2023-06-12), NEWF (no
 * history data)." With the name FastTrack sends, when it sends one.
 */
export function shortHistoryNote(
  label: string,
  securities: SecurityWithShortHistory[],
) {
  const listed = securities
    .map(({ symbol, name, startDate }) => {
      const history = startDate
        ? `history since ${startDate}`
        : "no history data";
      return `${symbol} (${name ? `${name}, ` : ""}${history})`;
    })
    .join(", ");
  return `† ${label} return not available: ${listed}.`;
}

/**
 * The footnotes at the bottom of the model, benchmark and sleeve pages: the
 * securities with no expense ratio ("Expense Ratio*"), and for each return
 * that is "N/A†", the securities whose history is too short for it. It reads
 * the performance `ModelPerformance` fetched (the same query key, never
 * fetching itself), and renders nothing when there is nothing to disclose.
 */
export const ModelPerformanceFootnote = ({
  model,
}: {
  model: Partial<ModelRequestSecurities>;
}) => {
  const { data, isError } = useQueryModelPerformance(model, {
    enabled: false,
  });
  // A failed refetch keeps the last `data`, which the panel no longer shows.
  if (!data || isError) return null;
  const withoutExpenseRatio = data.securitiesWithoutExpenseRatio ?? [];
  const shortHistories = RETURN_PERIODS.map(({ period, label }) => ({
    period,
    label,
    securities: shortHistoryFor(data, period),
  })).filter(({ securities }) => securities.length > 0);
  if (withoutExpenseRatio.length === 0 && shortHistories.length === 0) {
    return null;
  }
  return (
    <div className="text-muted small mt-3">
      {withoutExpenseRatio.length > 0 && (
        <p className="mb-1" data-testid="expense-ratio-footnote">
          {missingExpenseRatioNote(
            withoutExpenseRatio,
            data.expenseRatio != null,
          )}
        </p>
      )}
      {shortHistories.map(({ period, label, securities }) => (
        <p
          key={period}
          className="mb-1"
          data-testid={`short-history-footnote-${period}`}
        >
          {shortHistoryNote(label, securities)}
        </p>
      ))}
    </div>
  );
};

/** A return as the panel shows it: "N/A†" when it lacks history. */
function formatReturn(performance: ModelPerformanceData, period: ReturnPeriod) {
  const shown = formatMetricPercent(performance.returns[period], 2);
  return shortHistoryFor(performance, period).length > 0 ? (
    <>
      {shown}
      <span title="Some securities lack the history for this period; see the note at the bottom of the page">
        †
      </span>
    </>
  ) : (
    shown
  );
}

const ModelPerformance = ({
  model,
  isLoading = false,
}: {
  model: Partial<ModelRequestSecurities>;
  isLoading: boolean;
}) => {
  const {
    isPending: isPendingPerformance,
    isError: isErrorPerformance,
    data: dataPerformance,
    refetch,
  } = useQueryModelPerformance(model, { enabled: false });

  // The read waits for the form to settle: 0 ms while there is no response
  // yet, 1000 ms after the last change. The timer is cleared only when
  // `model` or `isLoading` changes, or on unmount: a render caused by the
  // query's own state (the response of an earlier read) must not cancel it.
  const queryClient = useQueryClient();
  const delay = isPendingPerformance ? 0 : 1000;
  useEffect(() => {
    if (isLoading) return undefined;
    const timer = window.setTimeout(() => {
      // A read still in flight with no data yet is reused by `refetch`, and
      // would answer for the body it was sent with: cancel it first.
      queryClient
        .cancelQueries({ queryKey: MODEL_PERFORMANCE_KEY })
        .then(() => refetch())
        .catch(() => undefined);
    }, delay);
    return () => window.clearTimeout(timer);
    // `delay`, `queryClient` and `refetch` are read when the timer is set; a change of
    // either alone (the response arriving) must not reset the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, isLoading]);

  return isLoading || isPendingPerformance ? (
    <Loading />
  ) : isErrorPerformance || typeof dataPerformance === "undefined" ? (
    <Alert variant="danger">Failed to load model performance</Alert>
  ) : (
    <div>
      <h4>Performance</h4>
      <dl>
        <dt>
          Expense Ratio
          {(dataPerformance.securitiesWithoutExpenseRatio ?? []).length > 0 && (
            <span title="Some securities have no expense ratio; see the note at the bottom of the page">
              *
            </span>
          )}
        </dt>
        <dd>{formatMetricPercent(dataPerformance.expenseRatio, 2)}</dd>
        <dt>Dividend Yield</dt>
        <dd>{formatMetricPercent(dataPerformance.dividendYield, 2)}</dd>
        <dt>1 Year</dt>
        <dd>{formatReturn(dataPerformance, "oneYear")}</dd>
        <dt>3 Years</dt>
        <dd>{formatReturn(dataPerformance, "threeYear")}</dd>
        <dt>5 Years</dt>
        <dd>{formatReturn(dataPerformance, "fiveYear")}</dd>
      </dl>
      <p>
        <FastTrackAttribution />
      </p>
    </div>
  );
};

export default ModelPerformance;
